/*
 * The orders table keeps its filters in the URL so a view can be linked and
 * survives a reload.
 *
 * Every helper here mutates a params object the caller owns rather than
 * returning a new one, because the hook applies several in a row against a
 * mutable ref — consecutive setters in the same tick would otherwise each start
 * from the pre-render URL and overwrite one another.
 */

/** Multi-select params are repeated keys, not comma-joined. */
export const getArrayParam = (params: URLSearchParams, key: string): string[] =>
  params.getAll(key).filter(Boolean);

/**
 * A single-value filter. Trimmed, and removed entirely when empty — a
 * present-but-blank param would read as a real filter matching nothing.
 *
 * Always resets paging: page 4 of the old result set is meaningless.
 */
export const applyStringParam = (params: URLSearchParams, key: string, value: string): void => {
  const normalized = value.trim();

  if (normalized) {
    params.set(key, normalized);
  } else {
    params.delete(key);
  }

  params.delete('page');
};

export const applyArrayParam = (params: URLSearchParams, key: string, values: string[]): void => {
  params.delete(key);

  values.filter(Boolean).forEach((value) => {
    params.append(key, value);
  });

  params.delete('page');
};

/** Both ends of a date range in one write, so neither clobbers the other. */
export const applyDateRange = (
  params: URLSearchParams,
  fromKey: string,
  toKey: string,
  from: string,
  to: string
): void => {
  if (from) {
    params.set(fromKey, from);
  } else {
    params.delete(fromKey);
  }

  if (to) {
    params.set(toKey, to);
  } else {
    params.delete(toKey);
  }

  params.delete('page');
};

export type OrgOrdersUrlFilters = {
  search: string;
  status: string[];
  productId: string[];
  locationId: string[];
  deliveryState: string[];
  pickupState: string[];
  pickupCity: string[];
  truckNumber: string[];
  driverName: string[];
  carrierId: string[];
  loadOption: string[];
  salesRep: string[];
  orderDateFrom: string;
  orderDateTo: string;
  pickupDateFrom: string;
  pickupDateTo: string;
  deliveryDateFrom: string;
  deliveryDateTo: string;
  tag: string[];
};

/**
 * URL key per filter, in one place.
 *
 * Four of them do not match their state name — `search` is `query`,
 * `productId` is `product`, `locationId` is `warehouse`, `driverName` is
 * `driver`, `carrierId` is `carrier`. Reader and writer previously spelled
 * these out separately fifteen times each; a single disagreement would make a
 * filter write to one key and read back from another, so it would survive a
 * reload as empty.
 */
export const URL_KEYS = {
  search: 'query',
  status: 'status',
  productId: 'product',
  locationId: 'warehouse',
  deliveryState: 'deliveryState',
  pickupState: 'pickupState',
  pickupCity: 'pickupCity',
  truckNumber: 'truckNumber',
  driverName: 'driver',
  carrierId: 'carrier',
  loadOption: 'loadOption',
  salesRep: 'salesRep',
  orderDateFrom: 'orderDateFrom',
  orderDateTo: 'orderDateTo',
  pickupDateFrom: 'pickupDateFrom',
  pickupDateTo: 'pickupDateTo',
  deliveryDateFrom: 'deliveryDateFrom',
  deliveryDateTo: 'deliveryDateTo',
  tag: 'tag',
} as const;

/** The filters a directly opened or refreshed URL should restore. */
export const readUrlFilters = (params: URLSearchParams): OrgOrdersUrlFilters => ({
  search: params.get(URL_KEYS.search) ?? '',
  status: getArrayParam(params, URL_KEYS.status),
  productId: getArrayParam(params, URL_KEYS.productId),
  locationId: getArrayParam(params, URL_KEYS.locationId),
  deliveryState: getArrayParam(params, URL_KEYS.deliveryState),
  pickupState: getArrayParam(params, URL_KEYS.pickupState),
  pickupCity: getArrayParam(params, URL_KEYS.pickupCity),
  truckNumber: getArrayParam(params, URL_KEYS.truckNumber),
  driverName: getArrayParam(params, URL_KEYS.driverName),
  carrierId: getArrayParam(params, URL_KEYS.carrierId),
  loadOption: getArrayParam(params, URL_KEYS.loadOption),
  salesRep: getArrayParam(params, URL_KEYS.salesRep),
  orderDateFrom: params.get(URL_KEYS.orderDateFrom) ?? '',
  orderDateTo: params.get(URL_KEYS.orderDateTo) ?? '',
  pickupDateFrom: params.get(URL_KEYS.pickupDateFrom) ?? '',
  pickupDateTo: params.get(URL_KEYS.pickupDateTo) ?? '',
  deliveryDateFrom: params.get(URL_KEYS.deliveryDateFrom) ?? '',
  deliveryDateTo: params.get(URL_KEYS.deliveryDateTo) ?? '',
  tag: getArrayParam(params, URL_KEYS.tag),
});
